import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '../lib/api';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await authAPI.forgotPassword({ email });
      setSent(true);
      toast.success('Reset link sent! Check your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* Left Panel - Branding */}
      <div className="auth-left hidden md:flex relative bg-slate-900 w-1/2 items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20 pointer-events-none"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1760239037245-a372db8630f3?crop=entropy&cs=srgb&fm=jpg&q=85)'
          }}
        />
        <div className="relative z-10 text-center px-12">
          <div className="w-20 h-20 bg-blue-600 rounded-sm mx-auto mb-8 flex items-center justify-center">
            <Shield size={40} className="text-white" />
          </div>
          <h2 className="font-secondary font-bold text-4xl text-white mb-4">
            Zentry
          </h2>
          <p className="text-slate-400 text-lg max-w-md">
            Enterprise-grade Governance, Risk, and Compliance platform.
            Map once, comply with many frameworks.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8 text-center">
            <div className="w-16 h-16 bg-slate-900 rounded-sm mx-auto mb-4 flex items-center justify-center">
              <Shield size={32} className="text-white" />
            </div>
          </div>

          {sent ? (
            <div className="text-center" data-testid="forgot-password-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <h1 className="font-secondary font-bold text-3xl text-slate-900 mb-2">
                Check your email
              </h1>
              <p className="text-slate-500 mb-6">
                If an account exists for <strong>{email}</strong>, we&apos;ve sent a password reset link.
                The link expires in 1 hour.
              </p>
              <p className="text-sm text-slate-400 mb-8">
                Didn&apos;t receive it? Check your spam folder or try again.
              </p>
              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full h-11 rounded-sm"
                  onClick={() => setSent(false)}
                  data-testid="try-again-btn"
                >
                  Try another email
                </Button>
                <Link to="/login" className="block">
                  <Button variant="ghost" className="w-full h-11 rounded-sm">
                    <ArrowLeft size={16} className="mr-2" />
                    Back to Sign In
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <>
              <h1 className="font-secondary font-bold text-3xl text-slate-900 mb-2">
                Forgot password?
              </h1>
              <p className="text-slate-500 mb-8">
                Enter your email and we&apos;ll send you a link to reset your password.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="email" className="text-slate-700">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="forgot-email-input"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1.5 h-11 rounded-sm"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="forgot-submit-btn"
                  disabled={loading}
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800 rounded-sm font-medium"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Mail size={18} />
                      Send Reset Link
                    </span>
                  )}
                </Button>
              </form>

              <p className="mt-6 text-center text-slate-500">
                Remember your password?{' '}
                <Link
                  to="/login"
                  data-testid="back-to-login-link"
                  className="text-blue-600 font-medium hover:underline"
                >
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
